// @ts-strict-ignore
import { useLazyQuery, useMutation } from "@apollo/client";
import useNotifier from "@dashboard/hooks/useNotifier";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import React, { useEffect, useState } from "react";

import { TargetedUser, TargetingScope, WalletOffer } from "../../types";
import { WALLET_OFFER_CREATE_MUTATION, WALLET_OFFER_UPDATE_MUTATION } from "../../walletMutations";
import { WALLET_CUSTOMER_SEARCH_QUERY, WALLET_OFFERS_QUERY } from "../../walletQueries";

const useStyles = makeStyles(
  (theme: any) => ({
    form: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2.5),
      paddingTop: theme.spacing(1),
    },
    radioGroup: {
      flexDirection: "row",
      gap: theme.spacing(3),
      marginTop: theme.spacing(0.5),
    },
    selectedUsersContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
      minHeight: 36,
      padding: theme.spacing(1),
      backgroundColor: theme.palette.background.default,
      borderRadius: theme.shape.borderRadius,
    },
    chip: {
      margin: theme.spacing(0.5),
    },
    sectionTitle: {
      fontWeight: 600,
      marginTop: theme.spacing(1),
      color: theme.palette.text.primary,
    },
    targetingBox: {
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
    },
  }),
  { name: "WalletOfferCreateDialog" },
);

interface WalletOfferCreateDialogProps {
  open: boolean;
  onClose: () => void;
  offerToEdit?: WalletOffer | null;
  onSuccess?: () => void;
}

export const WalletOfferCreateDialog: React.FC<WalletOfferCreateDialogProps> = ({
  open,
  onClose,
  offerToEdit,
  onSuccess,
}) => {
  const classes = useStyles();
  const notify = useNotifier();

  const isEditing = Boolean(offerToEdit);

  // Form states
  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [offerType, setOfferType] = useState<string>("FLAT_BONUS");
  const [minTopupAmount, setMinTopupAmount] = useState<string>("500");
  const [bonusAmount, setBonusAmount] = useState<string>("50");
  const [cashbackPercent, setCashbackPercent] = useState<string>("10");
  const [maxBonusCap, setMaxBonusCap] = useState<string>("");
  const [totalBudget, setTotalBudget] = useState<string>("");
  const [perUserLimit, setPerUserLimit] = useState<number>(1);
  const [promoValidityDays, setPromoValidityDays] = useState<number>(30);
  const [validFrom, setValidFrom] = useState<string>("");
  const [validTo, setValidTo] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);

  // 3-Tier User Targeting State
  const [targetingScope, setTargetingScope] = useState<TargetingScope>("ALL_USERS");
  const [selectedUsers, setSelectedUsers] = useState<TargetedUser[]>([]);
  const [userSearchInput, setUserSearchInput] = useState("");
  const [searchCustomersQuery, { data: searchData, loading: searchingUsers }] = useLazyQuery(
    WALLET_CUSTOMER_SEARCH_QUERY,
    { fetchPolicy: "network-only" },
  );

  const [createOffer, { loading: creating }] = useMutation(WALLET_OFFER_CREATE_MUTATION, {
    refetchQueries: [{ query: WALLET_OFFERS_QUERY, variables: { isStaffView: true } }],
  });

  const [updateOffer, { loading: updating }] = useMutation(WALLET_OFFER_UPDATE_MUTATION, {
    refetchQueries: [{ query: WALLET_OFFERS_QUERY, variables: { isStaffView: true } }],
  });

  const loading = creating || updating;

  const toLocalInputDateTime = (dateStr?: string | null): string => {
    if (!dateStr) return "";

    try {
      const d = new Date(dateStr);

      if (isNaN(d.getTime())) return "";

      const pad = (n: number) => String(n).padStart(2, "0");
      const year = d.getFullYear();
      const month = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  // Initialize or reset form
  useEffect(() => {
    if (open) {
      if (offerToEdit) {
        setCode(offerToEdit.code || "");
        setTitle(offerToEdit.title || "");
        setDescription(offerToEdit.description || "");
        setOfferType(offerToEdit.offerType?.toUpperCase() || "FLAT_BONUS");
        setMinTopupAmount(
          offerToEdit.minTopupAmount !== undefined && offerToEdit.minTopupAmount !== null
            ? String(offerToEdit.minTopupAmount)
            : "0",
        );
        setBonusAmount(
          offerToEdit.bonusAmount !== undefined && offerToEdit.bonusAmount !== null
            ? String(offerToEdit.bonusAmount)
            : "0",
        );
        setCashbackPercent(
          offerToEdit.cashbackPercent !== undefined && offerToEdit.cashbackPercent !== null
            ? String(offerToEdit.cashbackPercent)
            : "0",
        );
        setMaxBonusCap(
          offerToEdit.maxBonusCap !== undefined && offerToEdit.maxBonusCap !== null
            ? String(offerToEdit.maxBonusCap)
            : "",
        );
        setTotalBudget(
          offerToEdit.totalBudget !== undefined && offerToEdit.totalBudget !== null
            ? String(offerToEdit.totalBudget)
            : "",
        );
        setPerUserLimit(
          offerToEdit.perUserLimit !== undefined && offerToEdit.perUserLimit !== null
            ? Number(offerToEdit.perUserLimit)
            : 1,
        );
        setPromoValidityDays(
          offerToEdit.promoValidityDays !== undefined && offerToEdit.promoValidityDays !== null
            ? Number(offerToEdit.promoValidityDays)
            : 30,
        );
        setValidFrom(toLocalInputDateTime(offerToEdit.validFrom));
        setValidTo(toLocalInputDateTime(offerToEdit.validTo));
        setIsActive(offerToEdit.isActive !== false);

        // Normalize targeting scope
        const scope = offerToEdit.targetType
          ? (offerToEdit.targetType.toUpperCase() as TargetingScope)
          : offerToEdit.isTargeted
            ? offerToEdit.allowedUsersCount === 1
              ? "SINGLE_USER"
              : "MULTIPLE_USERS"
            : "ALL_USERS";

        setTargetingScope(scope);
        setSelectedUsers(offerToEdit.allowedUsers || []);
        setUserSearchInput("");
      } else {
        const now = new Date();
        const nextMonth = new Date();

        nextMonth.setDate(now.getDate() + 30);

        setCode("");
        setTitle("");
        setDescription("");
        setOfferType("FLAT_BONUS");
        setMinTopupAmount("500");
        setBonusAmount("50");
        setCashbackPercent("10");
        setMaxBonusCap("");
        setTotalBudget("");
        setPerUserLimit(1);
        setPromoValidityDays(30);
        setValidFrom(toLocalInputDateTime(now.toISOString()));
        setValidTo(toLocalInputDateTime(nextMonth.toISOString()));
        setIsActive(true);
        setTargetingScope("ALL_USERS");
        setSelectedUsers([]);
        setUserSearchInput("");
      }
    }
  }, [open, offerToEdit]);

  const handleSearchUsers = (query: string) => {
    if (query.trim().length >= 2) {
      searchCustomersQuery({
        variables: { query: query.trim(), first: 10 },
      });
    }
  };

  const handleAddUser = (user: TargetedUser | null) => {
    if (!user) return;

    if (targetingScope === "SINGLE_USER") {
      setSelectedUsers([user]);
    } else {
      if (!selectedUsers.some(u => u.id === user.id)) {
        setSelectedUsers([...selectedUsers, user]);
      }
    }
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!code.trim() || !title.trim()) {
      notify({ status: "error", text: "Offer code and display title are required." });

      return;
    }

    if (!validFrom || !validTo) {
      notify({ status: "error", text: "Start and end dates are required." });

      return;
    }

    if (targetingScope === "SINGLE_USER" && selectedUsers.length !== 1) {
      notify({
        status: "error",
        text: "Please select exactly 1 customer for Specific User targeting.",
      });

      return;
    }

    if (targetingScope === "MULTIPLE_USERS" && selectedUsers.length === 0) {
      notify({
        status: "error",
        text: "Please select at least 1 customer for Selected Users targeting.",
      });

      return;
    }

    // Prepare userIds list:
    // If ALL_USERS: pass [] (empty list tells backend to clear allowed_users)
    // If SINGLE_USER or MULTIPLE_USERS: pass array of IDs
    const userIds = targetingScope === "ALL_USERS" ? [] : selectedUsers.map(u => u.id);

    const inputData: any = {
      code: code.trim().toUpperCase(),
      title: title.trim(),
      description: description.trim(),
      offerType,
      minTopupAmount: parseFloat(minTopupAmount) || 0,
      bonusAmount: parseFloat(bonusAmount) || 0,
      cashbackPercent: parseFloat(cashbackPercent) || 0,
      maxBonusCap: maxBonusCap ? parseFloat(maxBonusCap) : null,
      validFrom: new Date(validFrom).toISOString(),
      validTo: new Date(validTo).toISOString(),
      totalBudget: totalBudget ? parseFloat(totalBudget) : null,
      perUserLimit: Number(perUserLimit) || 1,
      promoValidityDays: Number(promoValidityDays) || 30,
      isActive,
      userIds,
    };

    try {
      if (isEditing && offerToEdit) {
        const res = await updateOffer({
          variables: { id: offerToEdit.id, input: inputData },
        });

        if (res.errors && res.errors.length > 0) {
          notify({ status: "error", text: res.errors.map((err: any) => err.message).join(", ") });

          return;
        }

        if (!res.data?.walletOfferUpdate) {
          notify({ status: "error", text: "Failed to update offer: Action not permitted or request failed." });

          return;
        }

        const errors = res.data.walletOfferUpdate.errors;

        if (errors && errors.length > 0) {
          notify({ status: "error", text: errors.map((err: any) => err.message).join(", ") });

          return;
        }

        notify({ status: "success", text: `Offer ${code} updated successfully.` });
      } else {
        const res = await createOffer({
          variables: { input: inputData },
        });

        if (res.errors && res.errors.length > 0) {
          notify({ status: "error", text: res.errors.map((err: any) => err.message).join(", ") });

          return;
        }

        if (!res.data?.walletOfferCreate) {
          notify({ status: "error", text: "Failed to create offer: Action not permitted or request failed." });

          return;
        }

        const errors = res.data.walletOfferCreate.errors;

        if (errors && errors.length > 0) {
          notify({ status: "error", text: errors.map((err: any) => err.message).join(", ") });

          return;
        }

        notify({ status: "success", text: `Offer ${code} created successfully.` });
      }

      onClose();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      notify({ status: "error", text: err.message || "Failed to save wallet offer." });
    }
  };

  const customerOptions: TargetedUser[] = mapEdgesToItems(searchData?.search) || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEditing ? `Edit Wallet Offer (${offerToEdit?.code})` : "Create New Wallet Offer"}
        </DialogTitle>
        <DialogContent dividers className={classes.form}>
          {/* Basic Details */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Offer Promo Code"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                fullWidth
                required
                variant="outlined"
                placeholder="e.g. TOPUP50, FESTIVE15"
                helperText="Must be unique. Customers will enter this code."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Display Title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                fullWidth
                required
                variant="outlined"
                placeholder="e.g. Add ₹500, Get ₹50 Extra Free"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Marketing Description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                placeholder="Promotional banner description visible to customers"
              />
            </Grid>
          </Grid>

          {/* Offer Type & Financials */}
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Offer Financial Rules
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Offer Type"
                value={offerType}
                onChange={e => setOfferType(e.target.value as string)}
              >
                <MenuItem value="FLAT_BONUS">Flat Bonus</MenuItem>
                <MenuItem value="PERCENTAGE_CASHBACK">Percentage Cashback</MenuItem>
                <MenuItem value="FIRST_TOPUP">First Top-Up Only</MenuItem>
                <MenuItem value="WELCOME_BONUS">Welcome Bonus</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Min Top-Up Amount (₹)"
                type="number"
                value={minTopupAmount}
                onChange={e => setMinTopupAmount(e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Grid>
            {offerType === "PERCENTAGE_CASHBACK" ? (
              <>
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Cashback %"
                    type="number"
                    value={cashbackPercent}
                    onChange={e => setCashbackPercent(e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Max Cap (₹)"
                    type="number"
                    value={maxBonusCap}
                    onChange={e => setMaxBonusCap(e.target.value)}
                    fullWidth
                    variant="outlined"
                    placeholder="Optional"
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Bonus Credit (₹)"
                  type="number"
                  value={bonusAmount}
                  onChange={e => setBonusAmount(e.target.value)}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            )}
          </Grid>

          {/* User Targeting Configuration */}
          <Box className={classes.targetingBox}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, color: "#1e293b" }}>
              🎯 User Targeting & Eligibility
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure who is allowed to view and apply this top-up bonus offer.
            </Typography>

            <FormControl component="fieldset" style={{ marginTop: 8 }}>
              <RadioGroup
                row
                value={targetingScope}
                onChange={e => {
                  const val = e.target.value as TargetingScope;

                  setTargetingScope(val);

                  if (val === "ALL_USERS") {
                    setSelectedUsers([]);
                  }
                }}
                className={classes.radioGroup}
              >
                <FormControlLabel
                  value="ALL_USERS"
                  control={<Radio color="primary" />}
                  label="All Users (Public Offer)"
                />
                <FormControlLabel
                  value="SINGLE_USER"
                  control={<Radio color="primary" />}
                  label="Specific User (1 Customer)"
                />
                <FormControlLabel
                  value="MULTIPLE_USERS"
                  control={<Radio color="primary" />}
                  label="Selected Users (Multiple / VIPs)"
                />
              </RadioGroup>
            </FormControl>

            {targetingScope !== "ALL_USERS" && (
              <Box style={{ position: "relative", marginTop: 12 }}>
                <Box style={{ marginBottom: 6 }}>
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#334155" }}>
                    {targetingScope === "SINGLE_USER"
                      ? "Search & Select Customer"
                      : "Search Customers to Add"}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={userSearchInput}
                  placeholder="Type customer name, email, or phone number..."
                  helperText="Type at least 2 characters or digits to search"
                  onChange={e => {
                    const q = e.target.value;

                    setUserSearchInput(q);
                    handleSearchUsers(q);
                  }}
                  InputProps={{
                    endAdornment: searchingUsers ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null,
                  }}
                />

                {userSearchInput.trim().length >= 2 && customerOptions.length > 0 && (
                  <Paper
                    elevation={4}
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1300,
                      maxHeight: 220,
                      overflowY: "auto",
                      marginTop: 4,
                    }}
                  >
                    <List dense disablePadding>
                      {customerOptions.map(opt => {
                        const fullName = `${opt.firstName || ""} ${opt.lastName || ""}`.trim();
                        const isAlreadySelected = selectedUsers.some(u => u.id === opt.id);
                        const phoneDisplay = opt.phoneNumber ? `📞 ${opt.phoneNumber}` : null;
                        const emailDisplay = opt.email ? `✉️ ${opt.email}` : null;
                        const contactDetails = [emailDisplay, phoneDisplay]
                          .filter(Boolean)
                          .join("   ");

                        return (
                          <ListItem
                            key={opt.id}
                            button
                            disabled={isAlreadySelected}
                            onClick={() => {
                              handleAddUser(opt);
                              setUserSearchInput("");
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {fullName || "Customer"}
                                  </Typography>
                                  {opt.phoneNumber && (
                                    <Typography
                                      variant="caption"
                                      style={{ color: "#2563eb", fontWeight: 600 }}
                                    >
                                      {opt.phoneNumber}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="textSecondary" display="block">
                                  {contactDetails || opt.email}
                                </Typography>
                              }
                            />
                            {isAlreadySelected && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ marginLeft: 8 }}
                              >
                                Added
                              </Typography>
                            )}
                          </ListItem>
                        );
                      })}
                    </List>
                  </Paper>
                )}

                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginTop: 6 }}
                >
                  {targetingScope === "SINGLE_USER"
                    ? "Only this chosen user will see and be permitted to apply this promo code."
                    : `${selectedUsers.length} customer(s) currently selected in this targeted group.`}
                </Typography>

                <div className={classes.selectedUsersContainer}>
                  {selectedUsers.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" style={{ padding: "6px" }}>
                      No customers selected yet. Search above to add.
                    </Typography>
                  ) : (
                    selectedUsers.map(user => {
                      const userDisplay =
                        user.phoneNumber || user.email || user.firstName || "Customer";
                      const subText = user.firstName
                        ? ` (${user.firstName})`
                        : user.email && user.phoneNumber
                          ? ` (${user.email})`
                          : "";

                      return (
                        <Chip
                          key={user.id}
                          label={`${userDisplay}${subText}`}
                          onDelete={() => handleRemoveUser(user.id)}
                          color="primary"
                          variant="outlined"
                          size="small"
                          className={classes.chip}
                        />
                      );
                    })
                  )}
                </div>
              </Box>
            )}
          </Box>

          {/* Validity & Budget Controls */}
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Validity Dates & Safety Budgets
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Valid From"
                type="datetime-local"
                value={validFrom}
                onChange={e => setValidFrom(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Valid To"
                type="datetime-local"
                value={validTo}
                onChange={e => setValidTo(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Total Budget Limit (₹)"
                type="number"
                value={totalBudget}
                onChange={e => setTotalBudget(e.target.value)}
                fullWidth
                variant="outlined"
                placeholder="Optional overall cap"
                helperText="Offer auto-stops when exhausted"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Per User Usage Limit"
                type="number"
                value={perUserLimit}
                onChange={e => setPerUserLimit(Number(e.target.value))}
                fullWidth
                variant="outlined"
                helperText="Default 1 (single-use)"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Bonus Expiry (Days)"
                type="number"
                value={promoValidityDays}
                onChange={e => setPromoValidityDays(Number(e.target.value))}
                fullWidth
                variant="outlined"
                helperText="Days before credited bonus expires"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? "Saving..." : isEditing ? "Update Offer" : "Create Offer"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default WalletOfferCreateDialog;
